import { useMemo } from "react";
import type { CombinedContractListItem } from "@/features/hr/contracts/types";
import { parseDate, normalizeDateToStartOfDay, normalizeDateToEndOfDay, matchesDateRange, type DateRange } from "@/shared/utils";

interface FilterState {
  search: string;
  startDateRange: DateRange;
  statusFilter: string | null;
}

export function useContractsFilter(contracts: CombinedContractListItem[] | undefined, filters: FilterState): CombinedContractListItem[] {
  return useMemo(() => {
    const term = filters.search.trim().toLowerCase();
    const data = contracts ?? [];

    return data.filter((contract) => {
      const contractNumber = (contract.contract_number ?? "").toLowerCase();
      const candidateName = (contract.candidate_name ?? "").toLowerCase();
      const matchesSearch = !term || contractNumber.includes(term) || candidateName.includes(term);

      const matchesStatus =
        !filters.statusFilter ||
        (() => {
          if (filters.statusFilter === "null") {
            return contract.signing_status === null;
          }
          return contract.signing_status === Number(filters.statusFilter);
        })();

      const startDate = parseDate(contract.start_date);
      const matchesStartDate = matchesDateRange(
        startDate,
        filters.startDateRange,
        normalizeDateToStartOfDay,
        normalizeDateToEndOfDay,
      );

      return matchesSearch && matchesStatus && matchesStartDate;
    });
  }, [contracts, filters]);
}

export function hasActiveFilters(filters: FilterState): boolean {
  return (
    !!filters.search.trim() ||
    !!filters.startDateRange.start ||
    !!filters.startDateRange.end ||
    !!filters.statusFilter
  );
}

